import React, { useCallback, useState } from 'react';
import { Button } from '@material-ui/core';
import { IoPinSharp } from 'react-icons/io5';
import { getISOWeek, getDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { generateArray, getTotalDaysPerWeek } from '../../../utils/helper';
import { palette } from './palette';
import { useStyles } from './styles';

interface IWeekSchedule {
  data: number[];
  category: string;
  year: number;
  annualInterval?: [number, number, number, number];
  regularInterval?: [number, number, number, number];
  onSelectedChange?: (week: number) => void;
}

const WeekSchedule: React.FC<IWeekSchedule> = ({
  data,
  category,
  year,
  annualInterval,
  regularInterval,
  onSelectedChange,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const colors = palette.primary_main;

  const days = generateArray(1, 31, 1);
  const input = getTotalDaysPerWeek();
  const months = input.map(dt => dt.label);
  const currentWeek = getISOWeek(new Date());
  const currentYear = new Date().getFullYear();

  const getColor = (val: number): { bgColor: string; textColor: string } => {
    let bgColor = colors[0];
    let textColor = '#000';
    const totalInterval = annualInterval || [20, 30, 40, 50];
    const normalInterval = regularInterval || [2, 5, 7, 10];
    const interval = category === 'annual' ? totalInterval : normalInterval;

    if (val >= interval[0] && val < interval[1]) {
      bgColor = colors[1];
      textColor = '#000';
    } else if (val >= interval[1] && val < interval[2]) {
      bgColor = colors[2];
      textColor = '#000';
    } else if (interval[2] >= 40 && val < interval[3]) {
      bgColor = colors[3];
      textColor = '#FFF';
    } else {
      bgColor = colors[0];
      textColor = '#000';
    }
    return { bgColor, textColor };
  };

  const getLabel = (month: string, weekIndex: number): string => {
    const monthData = input.find(
      i => i.label.toLowerCase() === month.toLowerCase(),
    );

    let label = '';
    if (monthData) {
      const daysAmount = monthData.totalDays[weekIndex];
      if (daysAmount >= 5) {
        if (monthData.weeks[weekIndex] < 10) {
          label = `${t('screens:dimensioning.week')} 0${
            monthData.weeks[weekIndex]
          }`;
        } else {
          label = `${t('screens:dimensioning.week')} ${
            monthData.weeks[weekIndex]
          }`;
        }
      }
    }
    return label;
  };

  const getWidth = (month: string, weekIndex: number): string => {
    const monthData = input.find(
      i => i.label.toLowerCase() === month.toLowerCase(),
    );

    if (monthData) {
      const daysAmount = monthData.totalDays[weekIndex];

      const width = (daysAmount / 31) * 100;
      return `${width}%`;
    }

    return '100%';
  };

  const getMarkerPosition = (totalDays: number): string => {
    const dayOfWeek = getDay(new Date());
    const left = (dayOfWeek / totalDays) * 100 - 15;
    return `${left}%`;
  };

  const handleSelectWeek = useCallback(
    (week: number) => {
      setSelectedWeek(week);
      if (onSelectedChange) {
        onSelectedChange(week);
      }
    },
    [onSelectedChange],
  );

  return (
    <div className={classes.container}>
      <div className={classes.scheduleContainer}>
        <div className={classes.days}>
          {days.map((day, idx) => (
            <span
              key={day}
              className={classes.daysLabel}
              style={{
                borderBottomWidth:
                  idx + 1 === new Date().getDate() && year === currentYear
                    ? 2
                    : 0,
              }}
            >
              {day < 10 ? `0${day}` : day}
            </span>
          ))}
        </div>

        <div className={classes.months}>
          {months.map((month, idx) => {
            const monthTranslatedStr: any = `dates:months.${month.toLowerCase()}`;
            return (
              <span
                key={month}
                className={classes.monthsLabel}
                style={{
                  gridArea: `mon_${month.toLowerCase()}`,
                  borderBottomWidth:
                    idx === new Date().getMonth() && year === currentYear
                      ? 2
                      : 0,
                }}
              >
                {t(monthTranslatedStr).substring(0, 3).toUpperCase()}
              </span>
            );
          })}
        </div>

        <div className={classes.content}>
          {input.map((month, index) => (
            <div
              key={`${month.label}-values`}
              className={classes.data}
              style={{
                gridArea: month.label.toLowerCase(),
              }}
            >
              {input
                .find(i => i.label.toLowerCase() === month.label.toLowerCase())
                ?.totalDays.map((val, idx) => (
                  <Button
                    key={idx}
                    disabled={
                      (month.weeks[idx] === 53 && year <= currentYear) ||
                      (month.weeks[idx] < currentWeek && year <= currentYear)
                    }
                    className={classes.value}
                    style={{
                      backgroundColor: getColor(data[idx]).bgColor,
                      width: getWidth(month.label, idx),
                      opacity:
                        (month.weeks[idx] === 53 && year <= currentYear) ||
                        (month.weeks[idx] < currentWeek && year <= currentYear)
                          ? 0.6
                          : 1,
                      boxShadow:
                        currentWeek === month.weeks[idx] && year === currentYear
                          ? '0px 4px 4px rgba(0, 0, 0, 0.25)'
                          : 'none',
                    }}
                    onClick={() => handleSelectWeek(month.weeks[idx])}
                  >
                    {index === new Date().getMonth() &&
                      month.weeks[idx] === currentWeek &&
                      year === currentYear && (
                        <IoPinSharp
                          color="#007AFF"
                          size={28}
                          style={{
                            position: 'absolute',
                            top: -6,
                            left: getMarkerPosition(val),
                            zIndex: 100,
                          }}
                        />
                      )}
                    <span
                      className={classes.weeksLabel}
                      style={{
                        color: getColor(data[idx]).textColor,
                        fontWeight:
                          currentWeek === month.weeks[idx] ? 700 : 400,
                      }}
                    >
                      {getLabel(month.label, idx)}
                    </span>
                  </Button>
                ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeekSchedule;
